import { useEffect, useState } from 'react';
import type { MediaType } from './media';

/** Must match the id on the background embed iframe in PlayerControls. */
export function backgroundEmbedIframeId(bgEmbedKey: number): string {
  return `freq-locus-bg-${bgEmbedKey}`;
}

function formatClock(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

let youtubeApiPromise: Promise<void> | null = null;
function ensureYoutubeIframeApi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const w = window as Window & { YT?: { Player: new (id: string, opts: unknown) => YTPlayerLike } };
  if (w.YT?.Player) return Promise.resolve();
  if (youtubeApiPromise) return youtubeApiPromise;
  youtubeApiPromise = new Promise((resolve) => {
    const prev = (window as Window & { onYouTubeIframeAPIReady?: () => void }).onYouTubeIframeAPIReady;
    (window as Window & { onYouTubeIframeAPIReady?: () => void }).onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve();
    };
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    document.head.appendChild(tag);
  });
  return youtubeApiPromise;
}

type YTPlayerLike = {
  getCurrentTime: () => number;
  getDuration: () => number;
};

let soundcloudApiPromise: Promise<void> | null = null;
function ensureSoundcloudApi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const w = window as Window & { SC?: { Widget: (el: Element) => SoundCloudWidget } };
  if (w.SC?.Widget) return Promise.resolve();
  if (soundcloudApiPromise) return soundcloudApiPromise;
  soundcloudApiPromise = new Promise((resolve, reject) => {
    const tag = document.createElement('script');
    tag.src = 'https://w.soundcloud.com/player/api.js';
    tag.onload = () => resolve();
    tag.onerror = () => reject(new Error('SoundCloud api.js failed'));
    document.head.appendChild(tag);
  });
  return soundcloudApiPromise;
}

type SoundCloudWidget = {
  bind: (ev: string, fn: () => void) => void;
  getPosition: (cb: (ms: number) => void) => void;
  getDuration: (cb: (ms: number) => void) => void;
};

/**
 * Polls YouTube IFrame API / SoundCloud Widget while the hidden background embed plays,
 * so the deluxe player progress bar can move (the <audio> element is unused for embeds).
 */
export function useEmbedStreamProgress(opts: {
  mediaType: MediaType;
  embedListenPlaying: boolean;
  showVideoEmbed: boolean;
  bgEmbedKey: number;
}): { progress: number; currentLabel: string; durationLabel: string } {
  const { mediaType, embedListenPlaying, showVideoEmbed, bgEmbedKey } = opts;
  const [progress, setProgress] = useState(0);
  const [currentLabel, setCurrentLabel] = useState('0:00');
  const [durationLabel, setDurationLabel] = useState('0:00');

  useEffect(() => {
    if (!embedListenPlaying || showVideoEmbed || (mediaType !== 'youtube' && mediaType !== 'soundcloud')) {
      setProgress(0);
      setCurrentLabel('0:00');
      setDurationLabel('0:00');
      return;
    }

    const iframeId = backgroundEmbedIframeId(bgEmbedKey);
    let cancelled = false;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let ytPlayer: YTPlayerLike | null = null;
    let scWidget: SoundCloudWidget | null = null;

    const tickYoutube = () => {
      if (!ytPlayer || cancelled) return;
      try {
        const cur = ytPlayer.getCurrentTime();
        const dur = ytPlayer.getDuration();
        if (dur > 0) setProgress(Math.min(100, Math.max(0, (cur / dur) * 100)));
        setCurrentLabel(formatClock(cur));
        setDurationLabel(formatClock(dur));
      } catch {
        /* ignore */
      }
    };

    const tickSoundcloud = () => {
      if (!scWidget || cancelled) return;
      scWidget.getPosition((posMs) => {
        if (cancelled) return;
        scWidget!.getDuration((durMs) => {
          const cur = posMs / 1000;
          const dur = durMs / 1000;
          if (dur > 0) setProgress(Math.min(100, Math.max(0, (cur / dur) * 100)));
          setCurrentLabel(formatClock(cur));
          setDurationLabel(formatClock(dur));
        });
      });
    };

    const runYoutube = async () => {
      await ensureYoutubeIframeApi();
      if (cancelled) return;
      const w = window as Window & { YT?: { Player: new (id: string, opts: unknown) => YTPlayerLike } };
      if (!w.YT?.Player) return;

      let attempts = 0;
      const tryPlayer = () => {
        if (cancelled) return;
        const el = document.getElementById(iframeId);
        if (!el) {
          if (attempts++ < 40) requestAnimationFrame(tryPlayer);
          return;
        }
        try {
          new w.YT!.Player(iframeId, {
            events: {
              onReady: (e: { target: YTPlayerLike }) => {
                if (cancelled) return;
                ytPlayer = e.target;
                tickYoutube();
                intervalId = setInterval(tickYoutube, 400);
              },
            },
          });
        } catch {
          if (attempts++ < 40) window.setTimeout(tryPlayer, 100);
        }
      };
      tryPlayer();
    };

    const runSoundcloud = async () => {
      try {
        await ensureSoundcloudApi();
      } catch {
        return;
      }
      if (cancelled) return;
      const SC = (window as Window & { SC?: { Widget: (el: Element) => SoundCloudWidget } }).SC;
      if (!SC?.Widget) return;

      let attempts = 0;
      const tryWidget = () => {
        if (cancelled) return;
        const el = document.getElementById(iframeId);
        if (!el) {
          if (attempts++ < 40) requestAnimationFrame(tryWidget);
          return;
        }
        try {
          scWidget = SC.Widget(el);
          let started = false;
          const start = () => {
            if (cancelled || started) return;
            started = true;
            tickSoundcloud();
            intervalId = setInterval(tickSoundcloud, 400);
          };
          scWidget.bind('ready', start);
          window.setTimeout(() => {
            if (!cancelled && !started) start();
          }, 2500);
        } catch {
          if (attempts++ < 40) window.setTimeout(tryWidget, 100);
        }
      };
      tryWidget();
    };

    if (mediaType === 'youtube') void runYoutube();
    else void runSoundcloud();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      ytPlayer = null;
      scWidget = null;
      setProgress(0);
      setCurrentLabel('0:00');
      setDurationLabel('0:00');
    };
  }, [mediaType, embedListenPlaying, showVideoEmbed, bgEmbedKey]);

  return { progress, currentLabel, durationLabel };
}
